import React from 'react';
import classnames from 'classnames';
import { compose } from 'redux';

import { BUTTON_VARIANTS } from 'dpl/common/design_system/Button';
import {
  EVENT_CATEGORIES,
  EVENT_ACTION_IDS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import {
  BREEDER_MESSAGES_PATH,
  BUYER_MESSAGES_PATH
} from 'dpl/shared/constants/urls';
import { get } from 'dpl/shared/utils/object';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { trackEvent, trackConversion } from 'dpl/util/analytics';

interface AnalyticsProps {
  view?: string;
  view_feature?: string;
  view_feature_location?: string;
}

interface CurrentUserProps {
  isBreeder?: boolean | null;
  value?: {
    data?: {
      breeder_profiles_applied_to?: number[];
      buyer_profile_id?: number;
    };
  };
}

interface BreederProfileApplyButtonProps {
  breederProfileId: number;
  className?: string;
  currentUser: CurrentUserProps;
  isLoggedIn?: boolean | null;
  onClick: () => void;
  analytics?: AnalyticsProps;
  customCopy?: string | null;
}

const DEFAULT_ANALYTICS: AnalyticsProps = {
  view: ANALYTICS_VIEWS.BREEDER_PROFILE_PAGE,
  // We use this from a erb file so we are putting this here as a generic location
  view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.CTA
};

const APPLY_BUTTON_VARIANTS = {
  base: 'w-100 flex items-center justify-center ph8 pv3 br2 ba fw-medium font-16 lh-copy',
  primary: BUTTON_VARIANTS.primary,
  secondary: 'b--default bg-gray-100'
};

export default function BreederProfileApplyButton({
  breederProfileId,
  className,
  currentUser,
  isLoggedIn = null,
  onClick,
  analytics = DEFAULT_ANALYTICS,
  customCopy = 'Request info'
}: BreederProfileApplyButtonProps) {
  const appliedTo = get(
    currentUser,
    'value.data.breeder_profiles_applied_to',
    []
  );

  const initialInquiries = get(
    currentUser,
    'value.data.breeder_profiles_initial_inquiries',
    []
  );

  const initialInquiry = initialInquiries.find(
    (inquiry: any) =>
      Number(inquiry.breeder_profile_id) === Number(breederProfileId)
  );

  const canApply =
    typeof isLoggedIn === 'boolean' &&
    !appliedTo.includes(breederProfileId) &&
    !initialInquiry;
  const conversationId = initialInquiry?.conversation_id;

  const handleApplyClick = compose(
    trackEvent(EVENT_CATEGORIES.APPLY_TO_BREEDER),
    trackConversion(EVENT_ACTION_IDS.APPLY_TO_BREEDER)
  )(() => {
    const viewFeature =
      analytics.view_feature || ANALYTICS_VIEW_FEATURES.APPLY_BUTTON;

    setAnalyticsOrigin({
      originView: analytics.view,
      originViewFeature: viewFeature,
      originViewFeatureLocation: analytics.view_feature_location
    });

    onClick();
  }) as any;

  function handleClick() {
    if (canApply) {
      handleApplyClick();
      return;
    }

    if (conversationId) {
      const messagesPath = currentUser.isBreeder
        ? BREEDER_MESSAGES_PATH
        : BUYER_MESSAGES_PATH;
      window.location.href = `${messagesPath}/active/${conversationId}`;
    }
  }

  const ctaContent = canApply ? customCopy : 'Request sent';

  return (
    <button
      className={classnames(
        'BreederProfileApplyButton',
        className,
        APPLY_BUTTON_VARIANTS.base,
        canApply
          ? APPLY_BUTTON_VARIANTS.primary
          : APPLY_BUTTON_VARIANTS.secondary
      )}
      onClick={handleClick}
      type="button"
    >
      <span>{ctaContent}</span>
    </button>
  );
}
